import FullScreenModal from './FullScreenModal';
import ProfileForm from './ProfileForm';
import { useAuth } from '../hooks/useAuth';
import { useClickSound } from '../hooks/useClickSound';
import { getAuthUrl } from '../utils/api';
import type { Superteam, Country, Member } from '../types';

interface ProfileModalProps {
  isOpen?: boolean;
  onClose: () => void;
  superteams?: Superteam[];
  countries?: Country[];
  member?: Member;
}

export default function ProfileModal({ isOpen = true, onClose, superteams = [], countries = [], member }: ProfileModalProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { withClickSound } = useClickSound();

  const handleSignIn = async () => {
    try {
      const { url } = await getAuthUrl();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
    }
  };

  // If member prop is provided, show member profile view
  if (member) {
    return (
      <FullScreenModal isOpen={isOpen} onClose={onClose} title="Profile">
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            {member.xAvatarUrl && (
              <img
                src={member.xAvatarUrl}
                alt={member.name || member.xDisplayName}
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            )}
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white', margin: 0 }}>
                {member.name || member.xDisplayName}
              </h2>
              {member.xUsername && (
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: '4px 0 0 0' }}>
                  @{member.xUsername}
                </p>
              )}
            </div>
          </div>
          {member.bio && (
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
              {member.bio}
            </p>
          )}
          {member.skills && member.skills.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '8px' }}>Skills</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {member.skills.map((skill, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      background: 'rgba(20, 241, 149, 0.1)',
                      color: '#14F195',
                      fontSize: '12px',
                    }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </FullScreenModal>
    );
  }

  if (isLoading) {
    return (
      <FullScreenModal isOpen={isOpen} onClose={onClose} title="Profile">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="animate-spin" style={{
            width: '48px',
            height: '48px',
            border: '2px solid rgba(20,241,149,0.3)',
            borderTopColor: '#14F195',
            borderRadius: '50%',
          }} />
        </div>
      </FullScreenModal>
    );
  }

  if (!isAuthenticated) {
    return (
      <FullScreenModal isOpen={isOpen} onClose={onClose} title="Profile">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: 'white',
            marginBottom: '12px',
          }}>
            Sign in to Superwhere
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '16px',
            marginBottom: '32px',
            maxWidth: '400px',
          }}>
            Connect with Solana builders worldwide and join your local Superteam
          </p>
          <button
            onClick={withClickSound(handleSignIn)}
            style={{
              padding: '16px 32px',
              borderRadius: '12px',
              background: 'linear-gradient(to right, #14F195, #0da672)',
              color: 'black',
              border: 'none',
              fontWeight: 600,
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Sign in with X
          </button>
        </div>
      </FullScreenModal>
    );
  }

  return (
    <FullScreenModal isOpen={isOpen} onClose={onClose} title="Profile">
      <ProfileForm
        isOpen={true}
        onClose={onClose}
        superteams={superteams}
        countries={countries}
      />
    </FullScreenModal>
  );
}
